import { useCallback, useEffect, useMemo } from 'react';
import { Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { IUserAvatar } from '@interfaces/avatar';
import { ExperienceFeatures, IEvent } from '@interfaces/event';
import { IUser } from '@interfaces/user';
import { APP_LOGO } from '@components/AppLogo/interfaces';
import { DashboardButtonEnum } from '@constants/dashboard';
import { isEventFeatureEnabled, isSinglePlayerEvent } from '@helpers/events';
import {
  DEFAULT_AVATAR_ID,
  hasSelectableAvatars,
  isDefaultAvatar,
  withDefaultAvatar,
} from '@helpers/avatar';
import { MytaverseLogger } from '@helpers/logger';
import { CHAT_DRAWER_WIDTH, MINUTES_ALLOW_TO_JOIN } from '@constants';

import { env } from 'env';
import ROUTES from '@constants/routes';

export const useClientSpecificProfileInformation = () => {
  const { user } = useMytaverse();

  const profileUserName = useMemo(
    () => <Typography>{user.name}</Typography>,
    [user],
  );

  const profileEmail = useMemo(() => user.email, [user]);

  const profileGender = '';

  return { profileUserName, profileEmail, profileGender };
};

export const useClientSpecificVirtualAssistant = (): {
  icon: JSX.Element;
  label: string;
} | null => {
  return null;
};

export const useClientSpecificHasMultiplayerTag = () => {
  return { hasMultiplayerTag: false };
};

export const useClientSpecificPrivateChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isPrivateChatEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.PrivateChat),
    [currentEvent],
  );

  return { isPrivateChatEnabled };
};

export const useClientSpecificBroadcastChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isBroadcastEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Broadcast),
    [currentEvent],
  );

  return { isBroadcastEnabled };
};

export const useClientSpecificGlobalChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isGlobalChatEnabled = useMemo(() => {
    return isEventFeatureEnabled(currentEvent, ExperienceFeatures.GlobalChat);
  }, [currentEvent]);

  return { isGlobalChatEnabled };
};

export const useClientSpecificCameraVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isCameraEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Camera),
    [currentEvent],
  );

  return { isCameraEnabled };
};

export const useClientSpecificMicVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isMicEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Mic),
    [currentEvent],
  );

  return { isMicEnabled };
};

export const useClientSpecificParticipantsVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isParticipantsEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Participants),
    [currentEvent],
  );

  return { isParticipantsEnabled };
};

export const useClientSpecificOnlineUsersVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isOnlineUsersEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.OnlineUsers),
    [currentEvent],
  );

  return { isOnlineUsersEnabled };
};

export const useClientSpecificTeleportVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isTeleportFeatureEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Teleport),
    [currentEvent],
  );

  return { isTeleportFeatureEnabled };
};

/**
 * Whether the guided-tutorial HomeBar icon should render for the current
 * experience. Deliberately does not use `isEventFeatureEnabled` — that helper
 * treats a missing `features` key as enabled, whereas this flag defaults to
 * off until an admin explicitly opts an experience in from the CMS.
 *
 * @returns `isGuidedTutorialsEnabled` — true only when the experience's
 * `GUIDED_TUTORIALS` feature is explicitly enabled.
 */
export const useClientSpecificGuidedTutorialsVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isGuidedTutorialsEnabled = useMemo(
    () =>
      currentEvent?.settings?.features?.[ExperienceFeatures.GuidedTutorials]
        ?.enabled === true,
    [currentEvent],
  );

  return { isGuidedTutorialsEnabled };
};

export const useClientSpecificLaserPointerVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isLaserPointerEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.LaserPointer),
    [currentEvent],
  );

  return { isLaserPointerEnabled };
};

export const useClientSpecificMagnifierVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isMagnifierEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Magnifier),
    [currentEvent],
  );

  return { isMagnifierEnabled };
};

export const useClientSpecificEmojisVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isEmojisEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Emojis),
    [currentEvent],
  );

  return { isEmojisEnabled };
};

export const useClientSpecificReactionsVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isReactionsEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Reactions),
    [currentEvent],
  );

  return { isReactionsEnabled };
};

export const useClientSpecificMapVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isMapEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Map),
    [currentEvent],
  );

  return { isMapEnabled };
};

export const useClientSpecificFollowMeVisibility = () => {
  const { currentEvent, currentRoom } = useMytaverseEvent();

  // Following another avatar makes no sense in a single-player room, where the
  // event flag alone can still read as enabled on legacy experiences.
  const isFollowMeEnabled = useMemo(
    () =>
      isEventFeatureEnabled(currentEvent, ExperienceFeatures.FollowMe) &&
      !!currentRoom?.isMultiPlayer,
    [currentEvent, currentRoom],
  );

  return { isFollowMeEnabled };
};

export const useClientSpecificBroadcastChatInputVisibility = () => {
  return { canBroadcast: true };
};

export const useClientSpecificChatDrawerWidth = () => {
  const { isPrivateChatEnabled } = useClientSpecificPrivateChatVisibility();
  const { isBroadcastEnabled } = useClientSpecificBroadcastChatVisibility();

  if (isPrivateChatEnabled && isBroadcastEnabled) {
    return { chatDrawerWidth: CHAT_DRAWER_WIDTH.THREE_TABS };
  }

  return { chatDrawerWidth: CHAT_DRAWER_WIDTH.TWO_TABS };
};

export const useClientSpecificCarouselEventsTransform = () => {
  return useCallback((events: IEvent[]) => events, []);
};

export const useClientSpecificJoinEventSchedulerMinToJoin = () => {
  return MINUTES_ALLOW_TO_JOIN;
};

/**
 * Whether the user is still under the per-user avatar limit. The default
 * avatar rides along in the selection list but is not one of the user's own
 * avatars, so it must not consume a slot.
 *
 * @param listOfAvatarsAlreadyCreated - Avatars rendered on the selection step.
 * @returns True while another avatar can be created.
 */
export const useClientSpecificCanCreateNewAvatar = (
  listOfAvatarsAlreadyCreated: IUserAvatar[],
): boolean => {
  return (
    listOfAvatarsAlreadyCreated.filter((avatar) => !isDefaultAvatar(avatar))
      .length < parseInt(env.REACT_APP_MAX_NUMBER_OF_AVATARS || '3')
  );
};

/**
 * Per-avatar capabilities of the selection card. The default avatar is shared
 * infrastructure rather than a record of the user's, so it can be neither
 * edited nor deleted.
 *
 * @param avatar - Avatar the card renders.
 * @returns Flags and custom copy for the card's edit/delete actions.
 */
export const useClientSpecificCustomTextDeleteEditActionsOnAvatar = (
  avatar: IUserAvatar | null,
) => {
  const isDefault = isDefaultAvatar(avatar);

  return {
    canEdit: !isDefault,
    canDelete: !isDefault,
    cannotEditAvatarNotification: undefined as string | undefined,
    customDeleteAvatarTitle: '',
    customDeleteAvatarText: '',
  };
};

export const useClientSpecificHideGenericAvaturnType = () => {
  return false;
};

export const useClientSpecificHideRevokeAvatarConsentLink = () => {
  return false;
};

/**
 * Whether the "Create New Avatar" tile is hidden on the selection step.
 * Single-player experiences deliberately offer selection only: the user picks
 * an existing avatar or the default one, without being pulled into avatar
 * creation.
 *
 * @returns True when avatar creation must not be offered.
 */
export const useClientSpecificHideAvaturnCreation = () => {
  const { currentEvent } = useMytaverseEvent();

  // `currentEvent` is fetched asynchronously and nothing gates the avatar
  // routes on it, so it is undefined for the whole fetch window. Treating that
  // as multiplayer would flash a working creation tile inside a single-player
  // experience, so an unresolved experience hides it instead.
  return !currentEvent || isSinglePlayerEvent(currentEvent);
};

export const useClientSpecificHideAvatarPolicyModal = () => {
  return false;
};

export const useClientSpecificNoEventsNotification = () => {
  return false;
};

export const useClientSpecificAvatarDeletion = () => {
  return useCallback(async (_avatar: IUserAvatar) => {
    return;
  }, []);
};

export const useClientSpecificLogo = () => {
  return {
    logo: APP_LOGO.DEFAULT,
    shouldStickToTheTop: false,
  };
};

export const useClientSpecificDashboardButtonDisabled = (
  _button: DashboardButtonEnum,
) => {
  return false;
};

/**
 * Selection list for the avatar step. The default avatar is always offered
 * alongside the user's own avatars, so someone who already has an avatar can
 * switch back to the default one instead of being stuck with it (MV-1893).
 *
 * @param avatars - Avatars the user created.
 * @returns List to render on the selection step.
 */
export const useClientSpecificUserAvatars = (avatars: IUserAvatar[]) => {
  const { user } = useMytaverse();

  return useMemo(
    () => withDefaultAvatar(avatars, user?.defaultAvatar),
    [avatars, user?.defaultAvatar],
  );
};

export const useClientSpecificEventProvider = () => {
  return (Component: JSX.Element) => Component;
};

/**
 * Resolves the avatar to enter an experience with when no selection step runs
 * (direct entry / refresh): the active one, else the first created one, else
 * the default one.
 *
 * @param user - Profile holding `activeAvatarId`, `avatars` and `defaultAvatar`.
 * @returns Avatar to apply, or null when there is nothing to apply.
 */
const resolveSelectedAvatar = (user: IUser): IUserAvatar | null => {
  // The default avatar is not part of `user.avatars`, so a selection of it —
  // persisted as `activeAvatarId: 'default-avatar'` — has to be matched here.
  // Without this the fallbacks below would silently swap it for the user's
  // first own avatar, which is exactly what MV-1893 asks to be selectable away
  // from.
  if (
    user?.activeAvatarId === DEFAULT_AVATAR_ID &&
    user?.defaultAvatar?.avatarUrl
  ) {
    return user.defaultAvatar;
  }

  let selectedAvatar =
    user?.activeAvatarId &&
    user?.avatars?.find((avatar) => avatar.avatarId === user.activeAvatarId);

  if (!selectedAvatar && user?.avatars?.length > 0) {
    MytaverseLogger.log(
      'Active avatar not resolved, falling back to first avatar',
    );
    selectedAvatar = user?.avatars[0];
  }

  if (!selectedAvatar && user?.defaultAvatar?.avatarUrl) {
    MytaverseLogger.log('User has no active avatars, defaultAvatar is used');
    selectedAvatar = user?.defaultAvatar;
  }

  return selectedAvatar || null;
};

/**
 * Navigation applied once an experience is picked in the carousel. The avatar
 * selection step runs for every experience, single-player included (MV-1893),
 * so the user can keep the current avatar or switch to the default one in one
 * click. Only a single-player experience with nothing to select — no created
 * avatars and no default one — still skips straight to the device test, since
 * single player never offers avatar creation.
 *
 * @returns Callback taking the selected experience and navigating on.
 */
export const useClientSpecificSelectEvent = () => {
  const navigate = useNavigate();

  const { user } = useMytaverse();

  return useCallback(
    async (event: IEvent) => {
      if (isSinglePlayerEvent(event) && !hasSelectableAvatars(user)) {
        // An empty step would be a dead end here, so entering proceeds with
        // whatever UE falls back to; `useClientSpecificDirectEntryAvatarGate`
        // still applies an avatar if one turns up by the time the experience
        // loads.
        return navigate(ROUTES.DEVICES_TEST(event.eventId));
      }
      return navigate(ROUTES.SELECT_AVATAR_PROVIDER(event?.eventId));
    },
    [user?.activeAvatarId, user?.avatars, user?.defaultAvatar],
  );
};

export const useClientSpecificDirectEntryAvatarGate = () => {
  const { pathname } = useLocation();

  const {
    user,
    customAvatarUrl,
    setCurrentAvatarId,
    setCustomAvatarUrl,
    setUser,
  } = useMytaverse();
  const { currentEventId } = useMytaverseEvent();

  useEffect(() => {
    if (
      !user ||
      !currentEventId ||
      customAvatarUrl ||
      pathname !== ROUTES.EVENT_PAGE(currentEventId)
    ) {
      return;
    }

    const selectedAvatar = resolveSelectedAvatar(user);

    if (!selectedAvatar?.avatarUrl) {
      return;
    }

    const activeAvatarId = selectedAvatar.avatarId ?? null;
    setCurrentAvatarId(activeAvatarId);
    setCustomAvatarUrl(selectedAvatar.avatarUrl);
    setUser((prevState: IUser) => ({ ...prevState, activeAvatarId }));
  }, [user, currentEventId, customAvatarUrl, pathname]);
};

/**
 * Back navigation from the device test page: to the previous step, which is the
 * avatar selection — or straight to the experience carousel when that step was
 * skipped (single player with nothing to select), since going back to an empty
 * step would be a dead end.
 *
 * @returns Callback navigating one step back.
 */
export const useClientSpecificDeviceTestBackButton = () => {
  const navigate = useNavigate();
  const { currentEvent, currentEventId, currentSkin } = useMytaverseEvent();
  const { user } = useMytaverse();

  return useCallback(() => {
    const defaultNavigatePage = currentSkin
      ? ROUTES.SELECT_AVATAR_SKIN(currentEventId)
      : ROUTES.SELECT_AVATAR_PROVIDER(currentEventId);

    if (isSinglePlayerEvent(currentEvent) && !hasSelectableAvatars(user)) {
      return navigate(ROUTES.SELECT_EVENT);
    }
    return navigate(defaultNavigatePage);
  }, [currentEventId, currentEvent, currentSkin, user]);
};
